import React, { useState, useCallback, useRef } from 'react';
import {
  Eye,
  EyeOff,
  Lock,
  Unlock,
  GripVertical,
  Type,
  Image as ImageIcon,
  Square,
  Circle,
  ChevronUp,
  ChevronDown,
  Plus,
} from 'lucide-react';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '../../ui/tooltip';
import type { CanvasElement, CanvasElementType } from '../../../services/feedWatch';

const TYPE_ICONS: Record<CanvasElementType, React.ElementType> = {
  text: Type,
  image: ImageIcon,
  rect: Square,
  circle: Circle,
};

const TYPE_LABELS: Record<CanvasElementType, string> = {
  text: 'Text',
  image: 'Image',
  rect: 'Rectangle',
  circle: 'Circle',
};

interface LayersPanelProps {
  elements: CanvasElement[];
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  onUpdateElement: (index: number, updates: Partial<CanvasElement>) => void;
  onMoveLayer: (index: number, direction: 'up' | 'down') => void;
  onReorderLayer: (fromIndex: number, toIndex: number) => void;
  onAddElement?: (type: CanvasElementType) => void;
}

export function LayersPanel({
  elements,
  selectedIndex,
  onSelect,
  onUpdateElement,
  onMoveLayer,
  onReorderLayer,
  onAddElement,
}: LayersPanelProps) {
  // Display in reverse z-order (top layer first in the list)
  const reversed = [...elements].map((el, i) => ({ el, originalIndex: i })).reverse();

  // Drag state
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<number | null>(null);
  const dragNodeRef = useRef<number | null>(null);
  const [showAddMenu, setShowAddMenu] = useState(false);

  const handleDragStart = useCallback((e: React.DragEvent, originalIndex: number) => {
    dragNodeRef.current = originalIndex;
    setDragIndex(originalIndex);
    e.dataTransfer.effectAllowed = 'move';
    if (e.currentTarget instanceof HTMLElement) {
      e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, originalIndex: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragNodeRef.current !== null && dragNodeRef.current !== originalIndex) {
      setDropTarget(originalIndex);
    }
  }, []);

  const handleDragLeave = useCallback(() => {
    setDropTarget(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetOriginalIndex: number) => {
    e.preventDefault();
    if (dragNodeRef.current !== null && dragNodeRef.current !== targetOriginalIndex) {
      onReorderLayer(dragNodeRef.current, targetOriginalIndex);
    }
    setDragIndex(null);
    setDropTarget(null);
    dragNodeRef.current = null;
  }, [onReorderLayer]);

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setDropTarget(null);
    dragNodeRef.current = null;
  }, []);

  return (
    <TooltipProvider delayDuration={300}>
      <div className="w-[200px] shrink-0 border-r bg-background flex flex-col overflow-hidden">
        <div className="px-3 py-2 border-b bg-muted/30 flex items-center justify-between">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Layers</h3>
          {onAddElement && (
            <div className="relative">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setShowAddMenu((s) => !s)}
                    className="h-6 w-6 flex items-center justify-center rounded hover:bg-muted transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Add Element</TooltipContent>
              </Tooltip>
              {showAddMenu && (
                <div className="absolute right-0 top-full mt-1 bg-popover border rounded-lg shadow-lg py-1 z-20 min-w-[120px]">
                  {(['text', 'image', 'rect', 'circle'] as CanvasElementType[]).map((type) => {
                    const Icon = TYPE_ICONS[type];
                    return (
                      <button
                        key={type}
                        onClick={() => { onAddElement(type); setShowAddMenu(false); }}
                        className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-muted transition-colors"
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {TYPE_LABELS[type]}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {reversed.length === 0 ? (
            <div className="px-3 py-6 text-center">
              <p className="text-xs text-muted-foreground">No elements yet</p>
              <p className="text-[10px] text-muted-foreground/60 mt-1">Click + to add one</p>
            </div>
          ) : (
            <div className="py-1">
              {reversed.map(({ el, originalIndex }) => {
                const Icon = TYPE_ICONS[el.type];
                const isSelected = selectedIndex === originalIndex;
                const isVisible = el.visible !== false;
                const isLocked = el.locked === true;
                const name = el.name || TYPE_LABELS[el.type];
                const isDragging = dragIndex === originalIndex;
                const isDropTarget = dropTarget === originalIndex;

                return (
                  <div
                    key={el.id || originalIndex}
                    draggable
                    onDragStart={(e) => handleDragStart(e, originalIndex)}
                    onDragOver={(e) => handleDragOver(e, originalIndex)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, originalIndex)}
                    onDragEnd={handleDragEnd}
                    onClick={() => onSelect(originalIndex)}
                    className={`
                      group flex items-center gap-1.5 px-2 py-1.5 mx-1 rounded-lg cursor-pointer text-sm transition-colors
                      ${isSelected
                        ? 'bg-primary/10 text-foreground border border-primary/30'
                        : 'hover:bg-muted/50 text-muted-foreground border border-transparent'
                      }
                      ${!isVisible ? 'opacity-50' : ''}
                      ${isDragging ? 'opacity-30' : ''}
                      ${isDropTarget ? 'border-primary border-dashed !border-2' : ''}
                    `}
                  >
                    <GripVertical className="h-3 w-3 shrink-0 opacity-30 cursor-grab active:cursor-grabbing" />
                    <Icon className="h-3.5 w-3.5 shrink-0" />
                    <span className="flex-1 truncate text-xs">{name}</span>

                    <button
                      onClick={(e) => { e.stopPropagation(); onMoveLayer(originalIndex, 'up'); }}
                      className="h-5 w-5 flex items-center justify-center rounded hover:bg-muted/80 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Move up"
                    >
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onMoveLayer(originalIndex, 'down'); }}
                      className="h-5 w-5 flex items-center justify-center rounded hover:bg-muted/80 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Move down"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </button>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onUpdateElement(originalIndex, { visible: !isVisible } as Partial<CanvasElement>);
                          }}
                          className="h-5 w-5 flex items-center justify-center rounded hover:bg-muted/80"
                        >
                          {isVisible ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>{isVisible ? 'Hide' : 'Show'}</TooltipContent>
                    </Tooltip>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onUpdateElement(originalIndex, { locked: !isLocked } as Partial<CanvasElement>);
                          }}
                          className="h-5 w-5 flex items-center justify-center rounded hover:bg-muted/80"
                        >
                          {isLocked ? <Lock className="h-3 w-3 text-primary" /> : <Unlock className="h-3 w-3 opacity-30" />}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>{isLocked ? 'Unlock' : 'Lock'}</TooltipContent>
                    </Tooltip>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-3 py-2 border-t bg-muted/20">
          <p className="text-[10px] text-muted-foreground text-center">
            {elements.length} element{elements.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>
    </TooltipProvider>
  );
}
